import Api from "@/components/api/api";
import ToastProvider from "@/hooks/useToastProvider";
import { getUserData } from "@/redux/slices/userSlices";
import {
  SUPER_VOUCHER,
  SuperVoucherParamsType,
  SuperVoucherRegisterSubmitForm,
} from "@/utils/interface.global";
import { PhotoIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input, Typography, Select, Option } from "@material-tailwind/react";
import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { GrFormPreviousLink } from "react-icons/gr";
import { useSelector } from "react-redux";
import * as Yup from "yup";

const formSchema = Yup.object().shape({
  name: Yup.string()
    .min(4, "Họ và tên phải có ít nhất 6 ký tự")
    .nullable()
    .transform((x) => (x === "" ? undefined : x))
    .notRequired(),
  code: Yup.string().required("Vui lòng chọn loại voucher"),
  gia_tri_sudung: Yup.string()
    .nullable()
    .transform((x) => (x === "" ? undefined : x))
    .notRequired(),
  dv_sudung_kethop: Yup.string()
    .transform((x) => (x === "" ? undefined : x))
    .nullable()
    .notRequired(),
  gia_tri_dv_sudung_kethop: Yup.string()
    .transform((x) => (x === "" ? undefined : x))
    .nullable()
    .notRequired(),
  hinh_hoadon: Yup.mixed().nullable().notRequired(),
});

interface Props {
  tabActive: string | null;
}
const Activated: React.FC<Props> = ({ tabActive }: Props) => {
  const user = useSelector(getUserData);
  const [step, setStep] = useState(1);
  const [dataVoucher, setDataVoucher] = useState<null | any>(null);
  const [listCountVoucher, setListCountVoucher] = useState<any[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SuperVoucherRegisterSubmitForm>({
    mode: "onTouched",
    resolver: yupResolver(formSchema),
    defaultValues: {
      code: "",
      name: "",
    },
  });

  useEffect(() => {
    if (tabActive == "activated") {
      onCheckVoucher();
    }
  }, [tabActive]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);

      // Chuyển file thành base64
      const reader = new FileReader();
      reader.onloadend = () => {
        // reader.result chứa nội dung file dưới dạng base64
        const base64String = reader.result as string;

        // Loại bỏ phần prefix data:image/jpeg;base64, để chỉ lấy chuỗi base64 thuần túy
        const base64 = base64String.split(",")[1];
        setValue("hinh_hoadon", base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const onCheckVoucher = async () => {
    const reqUrl = "/supper-voucher/check-voucher";
    const params = { key: user.key_login, user_id: user.id };
    const res = await Api.post(reqUrl, params);
    if (res) {
      if (res.data.status == false) {
        ToastProvider({ type: "error", content: res.data.message });
      } else {
        setListCountVoucher(res.data.data);
      }
      setDataVoucher(res.data);
    }
  };

  const handleSubmitFormConfirm = (values: SuperVoucherRegisterSubmitForm) => {
    const {
      name,
      code,
      gia_tri_sudung,
      dv_sudung_kethop,
      gia_tri_dv_sudung_kethop,
      hinh_hoadon,
    } = values;
    if (
      name == undefined ||
      code == undefined ||
      gia_tri_sudung == undefined ||
      dv_sudung_kethop == undefined ||
      gia_tri_dv_sudung_kethop == undefined
    ) {
      ToastProvider({
        type: "error",
        content:
          "Vui lòng nhập đầy đủ thông tin. Hoặc bấm bỏ qua nếu chưa đủ thông tin",
      });
    } else {
      const params: SuperVoucherParamsType = {
        key: user.key_login,
        user_id: user.id,
        code: code,
        kh_name: name,
        gia_tri_sudung: gia_tri_sudung,
        dv_sudung_kethop: dv_sudung_kethop,
        gia_tri_dv_sudung_kethop: gia_tri_dv_sudung_kethop,
        hinh_hoadon: hinh_hoadon,
      };
      submitActiveVoucher(params);
    }
  };

  const submitActiveVoucher = async (params: SuperVoucherParamsType) => {
    try {
      const reqUrl = "/supper-voucher/apply-voucher";
      const res = await Api.post(reqUrl, params);
      if (res) {
        if (res.data.status == false) {
          ToastProvider({ type: "error", content: res.data.message });
        } else {
          ToastProvider({ type: "success", content: res.data.message });
          setDataVoucher(null);
          setStep(1);
          reset();
          onCheckVoucher();
        }
      }
    } catch (error: any) {
      ToastProvider({ type: "error", content: error.message });
    }
  };

  const handleBackButton = () => {
    setStep(1);
    setDataVoucher(null);
  };

  return (
    <>
      <div className="activated-container">
        {step == 2 && (
          <div className="w-full">
            <div className="cursor-pointer font-bold flex items-center gap-1 hover:cursor-pointer hover:underline">
              <GrFormPreviousLink />
              <span className="text-sm" onClick={handleBackButton}>
                Quay Lại
              </span>
            </div>
          </div>
        )}
        <p>Mã Cửa Hàng</p>
        <h1 className="text-red-500 font-bold text-xl sm:text-3xl pt-2 pb-4">
          {user ? user.code_shop : <></>}
        </h1>

        {step == 1 ? ( //Mới nhập Mã voucher
          <>
            <div className="list-table">
              <div className="flex flex-col pt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm font-light">
                  <thead className="border-b font-medium dark:border-neutral-500">
                    <tr>
                      <th scope="col" className="px-6 py-2">
                        LOẠI VOUCHER
                      </th>
                      <th scope="col" className="px-6 py-2 text-left">
                        VOUCHER CẦN SỬ DỤNG
                      </th>
                      <th scope="col" className="px-6 py-2 text-center">
                        VOUCHER ĐÃ SỬ DỤNG
                      </th>
                      <th scope="col" className="px-6 py-2 text-center">
                        SỐ LƯỢNG CÒN LẠI
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {listCountVoucher.map((voucher: any, index: number) => (
                      <tr
                        key={index}
                        className="border-b transition duration-300 ease-in-out hover:bg-white hover:text-red-500 hover:font-bold cursor-pointer"
                      >
                        <td className="whitespace-nowrap px-6 py-2 font-medium line-clamp-1">
                          {voucher.name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-2 font-medium">
                          {voucher.limit_id}
                        </td>
                        <td className="whitespace-nowrap px-6 py-2 font-medium text-center overflow-hidden text-ellipsis max-w-[200px]">
                          {voucher.count_used}
                        </td>
                        <td className="whitespace-nowrap px-6 py-2 font-medium text-center">
                          {voucher.avaiable}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <button
              className="check-another-activated mt-6 font-bold"
              onClick={() => {
                setStep(2);
              }}
            >
              Sử dụng Voucher
            </button>
          </>
        ) : step == 2 ? (
          <>
            {/* Form Nhập Thông Tin Khách hàng */}

            <div className="mt-5 w-full relative z-50">
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Voucher sử dụng"
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                    error={errors.code && true}
                    menuProps={{
                      className: "max-h-72 overflow-y-auto mt-1",
                      style: {
                        zIndex: 9999,
                        position: "absolute",
                        width: "100%",
                      },
                      animate: {
                        mount: { y: 0 },
                        unmount: { y: 25 },
                      },
                    }}
                    containerProps={{
                      className: "mb-6",
                    }}
                    className="z-50"
                  >
                    {SUPER_VOUCHER.map((voucher, index) => (
                      <Option key={index} value={voucher.value}>
                        {voucher.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </div>
            {errors.code && (
              <Typography
                variant="small"
                color="red"
                className="flex items-center gap-1 font-normal mx-2 mt-3"
              >
                <InformationCircleIcon className="w-4 h-4 -mt-px" />
                {errors.code.message as string}
              </Typography>
            )}
            <Input
              {...register("name")}
              variant="standard"
              color="gray"
              label="Tên khách hàng"
              error={errors.name && true}
            />
            {errors.name && (
              <Typography
                variant="small"
                color="red"
                className="flex items-center gap-1 font-normal mx-2 mt-3"
              >
                <InformationCircleIcon className="w-4 h-4 -mt-px" />
                {errors.name.message as string}
              </Typography>
            )}
            <div className="mt-3 w-full">
              <Input
                {...register("gia_tri_sudung")}
                variant="standard"
                color="gray"
                type="text"
                label="Giá trị hoá đơn sử dụng (Bắt buộc)"
                error={errors.gia_tri_sudung && true}
              />
              {errors.gia_tri_sudung && (
                <Typography
                  variant="small"
                  color="red"
                  className="flex items-center gap-1 font-normal mx-2 mt-3 text-xs"
                >
                  <InformationCircleIcon className="w-3 h-3 -mt-px" />
                  {errors.gia_tri_sudung.message as string}
                </Typography>
              )}
            </div>
            <div className="mt-3 w-full">
              <Input
                {...register("dv_sudung_kethop")}
                variant="standard"
                color="gray"
                type="text"
                label="Dịch vụ sử dụng kết hợp (Bắt buộc)"
                error={errors.dv_sudung_kethop && true}
              />
              {errors.dv_sudung_kethop && (
                <Typography
                  variant="small"
                  color="red"
                  className="flex items-center gap-1 font-normal mx-2 mt-3 text-xs"
                >
                  <InformationCircleIcon className="w-3 h-3 -mt-px" />
                  {errors.dv_sudung_kethop.message as string}
                </Typography>
              )}
            </div>
            <div className="mt-3 w-full">
              <Input
                {...register("gia_tri_dv_sudung_kethop")}
                variant="standard"
                color="gray"
                type="email"
                label="Số tiền được giảm giá (bắt buộc)"
                error={errors.gia_tri_dv_sudung_kethop && true}
                className="mb-3"
              />
              {errors.gia_tri_dv_sudung_kethop && (
                <Typography
                  variant="small"
                  color="red"
                  className="flex items-center gap-1 font-normal mx-2 my-3 text-xs"
                >
                  <InformationCircleIcon className="w-3 h-3 -mt-px" />
                  {errors.gia_tri_dv_sudung_kethop.message as string}
                </Typography>
              )}
            </div>
            <div className="relative mt-4 w-full">
              <div
                className={`border-b border-blue-gray-200 pt-4 pb-1.5 flex items-center justify-between cursor-pointer ${
                  errors.hinh_hoadon ? "border-red-500" : ""
                }`}
                onClick={handleFileInputClick}
              >
                <span
                  className={`text-sm transition-all ${
                    selectedFileName
                      ? "text-blue-gray-900"
                      : "text-blue-gray-500"
                  }`}
                >
                  {selectedFileName || "Hình ảnh hoá đơn"}
                </span>
                <PhotoIcon className="w-5 h-5 text-blue-gray-500" />
              </div>
              <input
                type="file"
                className="hidden"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
            {errors.hinh_hoadon && (
              <Typography
                variant="small"
                color="red"
                className="flex items-center gap-1 font-normal mx-2 my-3 text-xs"
              >
                <InformationCircleIcon className="w-3 h-3 -mt-px" />
                {errors.hinh_hoadon.message as string}
              </Typography>
            )}
            <button
              className="check-another-activated w-1/2 mt-5"
              onClick={handleSubmit(handleSubmitFormConfirm)}
            >
              Xác Nhận
            </button>
          </>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default Activated;
